import { faChevronDown } from "@jewlr/pro-solid-svg-icons/faChevronDown"
import { faChevronUp } from "@jewlr/pro-solid-svg-icons/faChevronUp"
import {
  Box,
  Button,
  Flex,
  FontAwesomeIcon,
  Image,
  Typography,
} from "@jewlr/storybook/core"
import { useHandleOutsideClick } from "@jewlr/storybook/helpers"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { useState, useRef } from "react"
import styled from "styled-components"

import useNavigation from "helpers/use-navigation"
import withCountrySwitch from "hoc/with-country-switch"
import countries from "site-data/portal/countries"

const SelectedCountry = styled(Button).attrs({
  bg: "primary",
  height: "28px",
  variant: "text",
  width: "100%",
})`
  padding: 0 8px;

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.white};
  }
`

const CountryDropdown = styled.div`
  background: ${(props) => props.theme.colors.primary};
  padding: 8px;
  width: 100%;
  z-index: 9999;
  ${(props) => props.theme.mediaQueries.tablet`
    position: absolute;
  `}
`

const CountryName = styled(Typography)`
  color: ${(props) => props.theme.colors.white};
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  line-height: 16px;
  text-align: left;
  width: 100%;
`

const CountryButton = styled(Button).attrs({
  display: "flex",
  variant: "text",
  width: "100%",
})`
  &:focus {
    outline: 1px solid ${(props) => props.theme.colors.white};
    outline-offset: 1px;
  }
`

const CountryOption = ({ closeDropdown, country, index, onClick }) => {
  const keyboardProps = useNavigation({ onEscape: closeDropdown })

  return (
    <CountryButton
      aria-selected="false"
      onClick={onClick}
      role="option"
      tabIndex={index === 0 ? "0" : "-1"}
      {...keyboardProps}
    >
      <Image
        alt=""
        height="16"
        mr={1}
        ratio="25 / 16"
        src={require(`images/layout/header/country/${country.site_country}.svg`)}
        width="25"
      />
      <CountryName>{country.human_country}</CountryName>
    </CountryButton>
  )
}

CountryOption.propTypes = {
  closeDropdown: PropTypes.func,
  country: PropTypes.object,
  index: PropTypes.number,
  onClick: PropTypes.func,
}

const CountrySelector = ({ doSwitch }) => {
  const countrySelectorRef = useRef()
  const [expanded, showOptions] = useState(false)

  const currentCountry = countries.find(
    ({ geo }) => geo === sl.config.site_country_for_analytics
  )
  useHandleOutsideClick({
    enabled: expanded,
    onOutsideClick: () => showOptions(false),
    ref: countrySelectorRef,
  })

  return (
    <Box
      aria-label="Change Country"
      position="relative"
      ref={countrySelectorRef}
    >
      <SelectedCountry
        aria-expanded={expanded}
        aria-haspopup="listbox"
        expanded={expanded}
        id="country-selector-button"
        onClick={() => showOptions(!expanded)}
      >
        <Flex alignItems="center">
          <Image
            alt=""
            height="16"
            mr={1}
            ratio="25 / 16"
            src={require(`images/layout/header/country/${currentCountry.site_country}.svg`)}
            width="25"
          />
          <CountryName>{currentCountry.human_country}</CountryName>
        </Flex>
        <FontAwesomeIcon
          color="white"
          fontSize="8px"
          icon={expanded ? faChevronUp : faChevronDown}
          ml="4px"
        />
      </SelectedCountry>
      {expanded && (
        <>
          <FocusScope contain restoreFocus>
            <CountryDropdown
              aria-labelledby="country-selector-button"
              id="country-selector-list"
              role="listbox"
            >
              {countries
                .filter(
                  ({ geo }) => geo !== sl.config.site_country_for_analytics
                )
                .map((country, index) => (
                  <CountryOption
                    closeDropdown={() => showOptions(false)}
                    country={country}
                    index={index}
                    key={country.geo}
                    onClick={() => doSwitch(country.geo)}
                  />
                ))}
            </CountryDropdown>
          </FocusScope>
        </>
      )}
    </Box>
  )
}

CountrySelector.propTypes = {
  doSwitch: PropTypes.func,
}

export default withCountrySwitch(CountrySelector)
